import { useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { MoreHorizontal } from 'lucide-react';

export interface TopBarMenuItem {
  id: string;
  label: string;
  icon?: ReactNode;
  /** When true, render in the destructive accent (e.g. archive / delete). */
  destructive?: boolean;
  /** Disabled items show but cannot be activated. */
  disabled?: boolean;
  onSelect: () => void;
}

interface TopBarMenuProps {
  items: TopBarMenuItem[];
  triggerLabel?: string;
}

/**
 * Compact ellipsis-menu trigger for the Todos top toolbar. Renders the
 * dropdown via a portal so it never gets clipped by the sticky header.
 *
 * Positioning is recomputed on each open so the menu always aligns to the
 * trigger's right edge — sticky scroll containers don't fight us.
 */
export function TopBarMenu({ items, triggerLabel = 'More actions' }: TopBarMenuProps) {
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; right: number } | null>(null);

  useEffect(() => {
    if (!open) return;
    const trigger = triggerRef.current;
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    setPos({
      top: rect.bottom + 6,
      right: Math.max(8, window.innerWidth - rect.right),
    });
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onPointer = (e: PointerEvent) => {
      const target = e.target as Node | null;
      if (!target) return;
      if (menuRef.current?.contains(target)) return;
      if (triggerRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    const onResize = () => setOpen(false);
    document.addEventListener('pointerdown', onPointer);
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onResize, true);
    return () => {
      document.removeEventListener('pointerdown', onPointer);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onResize, true);
    };
  }, [open]);

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={triggerLabel}
        className="p-2 rounded-xl text-[var(--color-text-secondary)] hover:bg-[var(--color-surface)] transition-colors"
        title={triggerLabel}
      >
        <MoreHorizontal size={18} />
      </button>

      {open && pos && typeof document !== 'undefined'
        ? createPortal(
            <div
              ref={menuRef}
              role="menu"
              className="fixed z-50 py-1 rounded-xl shadow-xl"
              style={{
                top: pos.top,
                right: pos.right,
                background: 'var(--color-surface-elevated)',
                border: '1px solid var(--color-border)',
                minWidth: 220,
                boxShadow: '0 12px 32px rgba(0, 0, 0, 0.18)',
              }}
            >
              {items.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  role="menuitem"
                  disabled={item.disabled}
                  onClick={() => {
                    if (item.disabled) return;
                    setOpen(false);
                    item.onSelect();
                  }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left transition-colors disabled:opacity-50 hover:bg-[var(--color-surface)]"
                  style={{
                    color: item.destructive ? 'var(--color-error)' : 'var(--color-text-primary)',
                  }}
                >
                  {item.icon ? <span className="shrink-0">{item.icon}</span> : null}
                  <span className="flex-1">{item.label}</span>
                </button>
              ))}
            </div>,
            document.body,
          )
        : null}
    </>
  );
}
